import { createFileRoute } from "@tanstack/react-router";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { normalizeAuthEmail } from "@/lib/authEmail";
import { resolveLandingRoute } from "@/lib/routeAfterLogin";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Client Login — Currency Thoughts" },
      {
        name: "description",
        content:
          "Sign in to the Currency Thoughts client portal to access your policies, documents, and Strategist messages.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Client Login — Currency Thoughts" },
      {
        property: "og:description",
        content: "Secure access to the Currency Thoughts client portal.",
      },
      { property: "og:url", content: "/login" },
    ],
    links: [{ rel: "canonical", href: "/login" }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    const normalizedEmail = normalizeAuthEmail(email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    setLoading(false);
    if (error) {
      setLoginError("If this is your first time entering the portal, use the password setup link from your email or send yourself a fresh setup link.");
      toast.error(
        "That email/password combination did not match. Use password setup to create or reset it.",
      );
      return;
    }
    const uid = data.user?.id;
    if (uid) {
      const { data: roleRows } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid);
      const roles = (roleRows ?? []).map((r) => r.role as string);

      // Audit
      await supabase.from("activity_logs").insert({
        user_id: uid,
        action: "login_success",
        target_type: "auth",
        metadata: { roles },
      });

      // Always use resolveLandingRoute — it prioritizes admin > agent > client,
      // so a user with multiple roles lands on their highest-privilege portal.
      // A ?redirect= param (same-origin path only) wins, so store checkout
      // returns the buyer to their cart with items intact.
      const raw =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("redirect")
          : null;
      const safeRedirect =
        raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
      const dest = safeRedirect ?? (await resolveLandingRoute(uid));
      window.location.assign(dest);
    }
  };

  return (
    <AuthLayout eyebrow="Secure Portal" heading="Sign in to your portal">
      <form onSubmit={onSubmit} className="space-y-5">
        <Field
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputMode="email"
          required
        />
        <Field
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            search={{ email: normalizeAuthEmail(email) }}
            className="font-sans text-[12px] text-bronze-600 hover:text-bronze-400"
          >
            Create or reset password
          </Link>
        </div>
        {loginError && (
          <div className="border border-bronze-600/30 bg-cream-50 p-4 font-sans text-[13px] leading-relaxed text-charcoal-500">
            <p>{loginError}</p>
            <Link
              to="/forgot-password"
              search={{ email: normalizeAuthEmail(email) }}
              className="mt-2 inline-block font-semibold text-bronze-600 hover:text-bronze-400"
            >
              Send setup link
            </Link>
          </div>
        )}
        <PrimaryButton type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? "Signing in…" : "Sign In"}
        </PrimaryButton>
        <p className="text-center font-sans text-[12px] leading-relaxed text-charcoal-500">
          Access to the Currency Thoughts portal is by invitation. If you've spoken with a
          Strategist and haven't received your secure link, please{" "}
          <Link to="/contact" className="text-bronze-600 hover:text-bronze-400 font-semibold">
            contact us
          </Link>
          .
        </p>
        <div className="mt-4 flex items-center justify-center gap-4 border-t border-bronze-600/30 pt-4">
          <Link
            to="/admin-login"
            className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-bronze-400"
          >
            Staff Sign In →
          </Link>
          <span className="text-bronze-600/40">·</span>
          <Link
            to="/agent"
            className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 hover:text-bronze-400"
          >
            Strategist Console →
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  autoComplete,
  inputMode,
  required,
}: {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  inputMode?: "email";
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        inputMode={inputMode}
        required={required}
        className="mt-2 w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none"
        style={{ borderRadius: "var(--radius-button)" }}
      />
    </div>
  );
}
